import { Sprite } from '../engine/assetLoader';
import { Direction } from '../engine/enums';
import { Renderable } from '../engine/renderable';
import Vector2d from '../engine/vector2d';
import Entity, { moveEntity } from '../entity';
import Maze from '../maze';
import State from '../state';

export const TILE_SIZE = 32;
const SPEED = .5;

const checkPosition = (state: State, player: Entity): void => {
  const { position } = player;
  const food = Maze.getFood(position, state);

  // TODO: Give powerup + score to right player
  if (food) {
    if (food.isPowerup) {
      player.hasPowerup = true;
      player.score += 50;
    } else {
      player.score += 10;
    }

    state.eatFood(new Vector2d(food.x, food.y));
  }

  if (player.hasPowerup) {
    state.players.filter(p => !p.hasPowerup && p !== player).forEach(p => {
      if (Vector2d.isSame(p.target, player.target)) {
        if (!p.isRespawning) {
          p.lives--;
          p.isRespawning = true;
          player.score += 200;
        }
      }
    });
  }
};

export default class PacMan implements Renderable<State> {
  public get name() {
    return `player-${this._player}`;
  }

  private state: State | undefined;
  private position: Vector2d | undefined;
  private frame: number = 0;
  private direction: Direction | undefined;
  private isRespawning: boolean = false;

  private bufferCanvas: HTMLCanvasElement;
  private bufferCtx: CanvasRenderingContext2D;

  constructor(private sprite: Sprite | undefined, private _player: number) {
    this.bufferCanvas = document.createElement('canvas');
    this.bufferCtx = this.bufferCanvas.getContext('2d')!;
  }

  private getFrame() {
    const { frame, isRespawning } = this;
    let anim = [1, 2, 3];
    let frameSpeed = .5;

    if (isRespawning && this.state) {
      anim = [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
      frameSpeed = .2;
      this.state.players[this._player].isRespawning = true;
    }

    let showFrame = Math.floor(frame * frameSpeed) % anim.length;
    if (isRespawning && anim[showFrame] === 14) {
      this.state?.respawnPlayer(this._player);
    }

    return anim[showFrame];
  }

  update(gameTime: number, state: State) {
    const player = state.players[this._player];
    if (player.isDead) {
      return;
    }

    const { position } = this;
    const { isRespawning } = player;

    if (!isRespawning) {
      moveEntity(player, SPEED, state);
    }

    if (position) {
      if (isRespawning || player.position.x !== position.x || player.position.y !== position.y) {
        this.frame++;
      }
    }

    if (isRespawning && !this.isRespawning) {
      this.frame = 0;
    }

    this.position = player.position;
    this.direction = player.direction;
    this.isRespawning = isRespawning;
    this.state = state;

    checkPosition(state, player);
  }

  render(gameTime: number, ctx: CanvasRenderingContext2D) {
    const { position, sprite, isRespawning } = this;
    const player = this.state?.players[this._player];

    if (!sprite || !position || player?.isDead) {
      return;
    }

    const { x, y } = position;

    let xPos = x * 16 - 8;
    let yPos = y * 16 - 8;

    ctx.save();
    ctx.translate(xPos, yPos);

    let offsetX = 0;
    let offsetY = 0;

    if (!isRespawning) {
      switch (this.direction) {
        case Direction.Right:
          ctx.rotate(0);
          break;
        case Direction.Left:
          ctx.rotate(180 * (Math.PI / 180));
          offsetX = -32;
          offsetY = -32;
          break;
        case Direction.Down:
          ctx.rotate(90 * (Math.PI / 180));
          offsetY = -32;
          break;
        case Direction.Up:
          ctx.rotate(-90 * (Math.PI / 180));
          offsetX = -32;
          break;
      }
    }

    const shouldFlash = player?.hasPowerup && gameTime % 30 < 15;
    const { bufferCtx } = this;

    bufferCtx.save();
    bufferCtx.clearRect(0, 0, TILE_SIZE, TILE_SIZE);

    bufferCtx.drawImage(
      sprite.sprite,
      TILE_SIZE * this.getFrame(),
      0,
      TILE_SIZE,
      TILE_SIZE,
      0,
      0,
      TILE_SIZE,
      TILE_SIZE
    );

    if (shouldFlash) {
      bufferCtx.fillStyle = '#2532fb';
      bufferCtx.globalCompositeOperation = 'source-in';
      bufferCtx.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
    }

    bufferCtx.restore();

    ctx.drawImage(this.bufferCanvas, offsetX, offsetY);

    ctx.restore();
  }
}
